/**
 * The drag strip on the right edge of a resizable <th>. Its parent must be positioned —
 * a `position: sticky` header cell already is, which is why P37 could drop the `relative`
 * the cell used to carry (`design.md` §9: `relative` on the same rule cancels sticky, and
 * only Tailwind's emission order was keeping that from biting).
 *
 * Ported from reporting_app/web/src/components/ui/ColumnResizeHandle.tsx; P37 added the
 * keyboard.
 *
 * `stopPropagation` on both mousedown and click is load-bearing: the header cell itself is
 * usually a sort toggle, and without it grabbing the handle would also re-sort the table.
 * (The click that ENDS a drag is swallowed separately — see `shouldSuppressClick` in
 * useResizableColumns.)
 *
 * =========================================================================
 * THE KEYBOARD (P37 — audit S2-17, WCAG 2.1.1)
 * =========================================================================
 * The handle was `role="separator"` with `onMouseDown` and `onDoubleClick` and nothing
 * else: the width could not be changed or reset without a mouse. It is now the ARIA
 * window-splitter pattern — focusable, with `aria-valuenow/min/max`, Arrow keys to move it
 * (Shift for a bigger step) and Home to put it back. `onNudge` is optional so a caller
 * that does not offer it keeps the old mouse-only handle rather than a focusable control
 * that does nothing.
 *
 * It costs one tab stop per column, which is the honest price of the requirement: the
 * alternative on the table was to declare the handle mouse-only in the reference's
 * deviation log, and a control only a mouse can reach is the finding, not the fix.
 */
import { FOCUS_RING_TIGHT } from './focus';
import { MAX_COL_WIDTH, MIN_COL_WIDTH } from '../../hooks/useResizableColumns';

/** One arrow press, and one Shift+arrow press. Both are whole pixels, not a ratio. */
const STEP = 8;
const BIG_STEP = 32;

interface Props {
    label: string;
    active: boolean;
    onStart: (clientX: number) => void;
    onReset: () => void;
    /** The current width, for `aria-valuenow`. Omitted = the handle stays mouse-only. */
    width?: number;
    /** Change the width by `delta` px. Omitted = the handle stays mouse-only. */
    onNudge?: (delta: number) => void;
}

export function ColumnResizeHandle({ label, active, onStart, onReset, width, onNudge }: Props) {
    const keyboard = typeof onNudge === 'function';
    return (
        <span
            role="separator"
            aria-orientation="vertical"
            aria-label={`Resize ${label}`}
            tabIndex={keyboard ? 0 : undefined}
            aria-valuenow={keyboard ? width : undefined}
            aria-valuemin={keyboard ? MIN_COL_WIDTH : undefined}
            aria-valuemax={keyboard ? MAX_COL_WIDTH : undefined}
            onMouseDown={e => {
                e.preventDefault();
                e.stopPropagation();
                onStart(e.clientX);
            }}
            onClick={e => e.stopPropagation()}
            onDoubleClick={e => { e.stopPropagation(); onReset(); }}
            onKeyDown={e => {
                if (!onNudge) return;
                const step = e.shiftKey ? BIG_STEP : STEP;
                if (e.key === 'ArrowLeft') { e.preventDefault(); e.stopPropagation(); onNudge(-step); }
                else if (e.key === 'ArrowRight') { e.preventDefault(); e.stopPropagation(); onNudge(step); }
                else if (e.key === 'Home' || e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); onReset(); }
            }}
            title={keyboard
                ? `Drag to resize ${label} · double-click or Home to reset · arrow keys move it`
                : 'Drag to resize · double-click to reset'}
            className={`absolute right-0 top-0 z-10 h-full w-2 cursor-col-resize border-r-2 ${FOCUS_RING_TIGHT} ${
                active ? 'border-info' : 'border-transparent hover:border-info-100'
            }`}
        />
    );
}
